import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertTriangle, CheckCircle, XCircle, RefreshCw, Globe, Shield, Zap } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { useChatbotConfig } from "../../hooks/useChatbotConfig";

export function TroubleshootingTab() {
  const { t } = useTranslation('business', { keyPrefix: 'communication' });
  const { config } = useChatbotConfig();
  const isConfigured = Boolean(config.businessId && config.isEnabled);

  const commonIssues = [
    {
      id: 'widget-not-appearing',
      title: t('widgetNotAppearing'),
      severity: 'high',
      icon: <XCircle className="w-5 h-5 text-red-600" />,
      symptoms: [
        t('noChatbotWidgetVisible'),
        t('scriptLoadsButNothingHappens'),
        t('consoleShowsNoErrors')
      ],
      causes: [
        t('scriptPlacedInWrongLocation'),
        t('configurationNotSaved'),
        t('businessIdIncorrect'),
        t('websiteCachingIssues')
      ],
      solutions: [
        {
          title: 'Verify Script Placement',
          steps: [
            'Ensure script is before closing </body> tag',
            'Check script URL is correct',
            'Verify script has async attribute'
          ],
          code: `<!-- Correct placement -->
<script src="https://wildy-backend-production.up.railway.app/api/chatbot/YOUR_ID/widget.js" async></script>
</body>`
        },
        {
          title: 'Check Configuration',
          steps: [
            'Go to Communication Dashboard',
            'Verify chatbot is enabled',
            'Save configuration again',
            'Copy fresh embed code'
          ]
        },
        {
          title: 'Clear Cache',
          steps: [
            'Clear browser cache (Ctrl+F5)',
            'Clear website cache if using CDN',
            'Test in incognito/private mode'
          ]
        }
      ]
    },
    {
      id: 'cors-errors',
      title: 'CORS (Cross-Origin) Errors',
      severity: 'medium',
      icon: <Shield className="w-5 h-5 text-amber-600" />,
      symptoms: [
        'Console error: "CORS policy blocked"',
        'Widget loads but can\'t send messages',
        'Network requests failing'
      ],
      causes: [
        'Domain not authorized',
        'Incorrect API configuration',
        'Local development issues'
      ],
      solutions: [
        {
          title: 'Check Domain Authorization',
          steps: [
            'Verify your domain is added to authorized domains',
            'Check domain spelling and subdomain', 
            'Ensure HTTPS/HTTP protocol matches',
            'Contact Wildy staff to add your domain to CUSTOMER_DOMAINS'
          ]
        },
        {
          title: 'Local Development',
          steps: [
            'Use localhost:3000 or similar for testing',
            'Contact support to add development domains',
            'Use test page for initial testing'
          ]
        }
      ]
    },
    {
      id: 'csp-violations',
      title: 'Content Security Policy (CSP) Issues',
      severity: 'medium',
      icon: <Shield className="w-5 h-5 text-amber-600" />,
      symptoms: [
        'Console error: "Refused to execute inline script"',
        'Widget partially loads',
        'Styling issues'
      ],
      causes: [
        'Strict CSP headers',
        'Inline script restrictions',
        'External domain blocked'
      ],
      solutions: [
        {
          title: 'Update CSP Headers',
          steps: [
            'Add wildy-backend-production.up.railway.app to script-src',
            'Allow unsafe-inline for development',
            'Contact developer to update headers'
          ],
          code: `Content-Security-Policy: script-src 'self' 'unsafe-inline' https://wildy-backend-production.up.railway.app;`
        }
      ]
    },
    {
      id: 'performance-issues',
      title: 'Performance & Loading Issues',
      severity: 'low',
      icon: <Zap className="w-5 h-5 text-blue-600" />,
      symptoms: [
        'Slow page loading',
        'Widget takes time to appear',
        'Multiple script loads'
      ],
      causes: [
        'Script not async',
        'Multiple widget instances',
        'Large page size'
      ],
      solutions: [
        {
          title: 'Optimize Loading',
          steps: [
            'Ensure async attribute is present',
            'Place script at end of body',
            'Avoid loading widget multiple times'
          ]
        },
        {
          title: 'Check for Duplicates',
          steps: [
            'Search codebase for multiple widget scripts',
            'Remove duplicate implementations',
            'Use single integration point'
          ]
        }
      ]
    },
    {
      id: 'domain-whitelisting',
      title: 'Domain Whitelisting Required',
      severity: 'high',
      icon: <Shield className="w-5 h-5 text-purple-600" />,
      symptoms: [
        'Chatbot widget not loading on your website',
        'CORS errors mentioning blocked origin',
        'Network requests failing to backend server'
      ],
      causes: [
        'Your domain is not in the CUSTOMER_DOMAINS whitelist',
        'Security policy blocks unauthorized domains',
        'New domain not yet added to backend configuration'
      ],
      solutions: [
        {
          title: 'Request Domain Authorization',
          steps: [
            'Contact Wildy support team via chat or email',
            'Provide your exact domain (e.g., yoursite.com or subdomain.yoursite.com)',
            'Include both www and non-www versions if needed',
            'Wait for confirmation that domain is added to CUSTOMER_DOMAINS',
            'Test your integration after confirmation'
          ]
        },
        {
          title: 'Understanding Domain Security',
          steps: [
            'CUSTOMER_DOMAINS is a security feature to prevent unauthorized use',
            'Only approved domains can load the chatbot widget',
            'This protects your chatbot and ensures proper usage tracking',
            'Each client domain must be individually authorized'
          ]
        },
        {
          title: 'Common Domain Formats',
          steps: [
            'Production: yourcompany.com',
            'Staging: staging.yourcompany.com', 
            'Netlify: your-site-name.netlify.app',
            'Vercel: your-project.vercel.app',
            'Local development: localhost:3000 (contact support)'
          ],
          code: `Example CUSTOMER_DOMAINS entry:
"yoursite.com,www.yoursite.com,staging.yoursite.com"`
        }
      ]
    }
  ];

  const diagnosticSteps = [
    {
      title: t('checkBrowserConsole'),
      description: t('checkBrowserConsoleDesc'),
      icon: <AlertTriangle className="w-5 h-5 text-orange-600" />
    },
    {
      title: t('verifyNetworkRequests'),
      description: t('verifyNetworkRequestsDesc'),
      icon: <Globe className="w-5 h-5 text-blue-600" />
    },
    {
      title: t('testInIncognitoMode'),
      description: t('testInIncognitoModeDesc'),
      icon: <RefreshCw className="w-5 h-5 text-green-600" />
    },
    {
      title: t('checkScriptPlacement'),
      description: t('checkScriptPlacementDesc'),
      icon: <CheckCircle className="w-5 h-5 text-emerald-600" />
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center p-6 rounded-xl bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200/60">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-500/25">
          <AlertTriangle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('troubleshootingGuide')}</h2>
        <p className="text-slate-600 max-w-2xl mx-auto mb-4">
          {t('troubleshootingGuideDesc')}
        </p>
      </div>

      {/* Quick Diagnostic Steps */}
      <Card className="border border-blue-200/60 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-blue-600" />
            {t('quickDiagnosticSteps')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {diagnosticSteps.map((step, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200">
                {step.icon}
                <div>
                  <h4 className="font-semibold text-slate-900">{step.title}</h4>
                  <p className="text-sm text-slate-700">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Common Issues */}
      <Card className="border border-slate-200/60 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
            Common Issues & Solutions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="space-y-4">
            {commonIssues.map((issue) => (
              <AccordionItem key={issue.id} value={issue.id} className="border border-slate-200 rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    {issue.icon}
                    <div className="text-left">
                      <div className="font-semibold">{issue.title}</div>
                      <Badge 
                        variant={issue.severity === 'high' ? 'destructive' : issue.severity === 'medium' ? 'default' : 'secondary'}
                        className="text-xs mt-1"
                      >
                        {issue.severity} priority
                      </Badge>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-4">
                  {/* Symptoms */}
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <h5 className="font-medium text-red-900 mb-2">🔍 Symptoms:</h5>
                    <ul className="text-sm text-red-800 space-y-1">
                      {issue.symptoms.map((symptom, index) => (
                        <li key={index}>• {symptom}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Causes */}
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <h5 className="font-medium text-amber-900 mb-2">⚠️ Common Causes:</h5>
                    <ul className="text-sm text-amber-800 space-y-1">
                      {issue.causes.map((cause, index) => (
                        <li key={index}>• {cause}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Solutions */}
                  <div className="space-y-4">
                    <h5 className="font-medium text-green-900">✅ Solutions:</h5>
                    {issue.solutions.map((solution, solutionIndex) => (
                      <div key={solutionIndex} className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <h6 className="font-medium text-green-900 mb-2">{solution.title}</h6>
                        <ol className="text-sm text-green-800 space-y-1 mb-3">
                          {solution.steps.map((step, stepIndex) => (
                            <li key={stepIndex}>{stepIndex + 1}. {step}</li>
                          ))}
                        </ol>
                        {solution.code && (
                          <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto">
                            <code>{solution.code}</code>
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Test Your Integration */}
      <Card className="border border-emerald-200/60 bg-emerald-50/50">
        <CardContent className="p-6 text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Still Having Issues?</h3>
          <p className="text-slate-600 mb-4">Test your integration or contact our support team for help.</p>
          <div className="flex justify-center gap-4">
            <Button 
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-lg"
              onClick={() => window.open(`/chatbot-test.html?businessId=${config.businessId}`, '_blank')}
              disabled={!isConfigured}
            >
              <Globe className="w-4 h-4 mr-2" />
              Test Integration
            </Button>
            <Button variant="outline" className="shadow-sm">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Prevention Tips */}
      <Alert className="border-blue-200 bg-blue-50">
        <CheckCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>💡 Prevention Tips:</strong> Always test in a staging environment first, keep your embed code up to date, and monitor the browser console for any warnings during integration.
        </AlertDescription>
      </Alert>
    </div>
  );
} 